import { useEffect, useMemo } from 'react';
import * as Dfns from 'date-fns/fp';
import { Spinner } from '@Components/Spinner/Spinner';
import { useSetError } from '@src/redux/modals';
import { isValidTimelineEntry, OrderByOptions } from '../../OfflineInputs/OfflineInputsUtils';
import { TimelineBarChart } from '../../OfflineInputs/TimelineBarChart';
import { labelUnidentifiedPromos } from '../context-var-utils';
import { usePromoTimeline } from '../hooks/usePromoTimeline';

// Same sizing as the Offline Inputs Promos tab: a year fills the card, and each month
// past twelve adds width (scrolling sideways); each promo period gets a row.
const WIDTH_PER_MONTH = 800 / 12;
const MIN_HEIGHT_PER_ENTRY = 44;
// The month labels above the rows.
const AXIS_HEIGHT = 40;

// The chart reports its row count; this card counts the rows itself (to size the chart and
// pick the empty state before it renders), so it ignores the report.
const ignoreRowCount = () => {};

interface PromoCalendarProps {
  company: string;
  // The model's fencepost window, YYYY-MM-DD.
  start: string;
  end: string;
}

// The promo periods encoded on Offline Inputs' Promos tab, over the model's modeling
// range: the source the Promo Flag is built from, to check its time series against.
// Read-only -- promos are edited on Offline Inputs itself.
export const PromoCalendar = ({ company, start, end }: PromoCalendarProps) => {
  const { data, isLoading, error } = usePromoTimeline(company);
  const setError = useSetError();
  const entries = useMemo(() => labelUnidentifiedPromos(data ?? []), [data]);
  const dateRange = useMemo(() => ({ start, end }), [start, end]);
  // The chart's own filter (isValidTimelineEntry), applied here to count its rows.
  const visibleCount = useMemo(
    () =>
      entries.filter(entry => isValidTimelineEntry(entry, Dfns.parseISO(start), Dfns.parseISO(end)))
        .length,
    [entries, start, end]
  );

  const width = useMemo(() => {
    const months = Dfns.differenceInCalendarMonths(Dfns.parseISO(start))(Dfns.parseISO(end)) + 1;
    return `calc(100% + ${Math.max(0, months - 12) * WIDTH_PER_MONTH}px)`;
  }, [start, end]);

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load promos",
        message: `Failed to load promos from Offline Inputs: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  if (isLoading) {
    return (
      <div className='data-qa-promo-calendar__state'>
        <Spinner size={24} />
      </div>
    );
  }
  if (error) {
    return (
      <div className='data-qa-promo-calendar__state'>
        Couldn&apos;t load promos from Offline Inputs.
      </div>
    );
  }

  if (visibleCount === 0) {
    return (
      <div className='data-qa-promo-calendar__state'>
        No promos encoded on Offline Inputs between {start} and {end}.{' '}
        <a
          href={`https://blisspoint.tinuiti.com/${company}/dtc-mmm/offline-inputs`}
          target='_blank'
          rel='noreferrer'
        >
          Open Offline Inputs
        </a>
      </div>
    );
  }

  return (
    <div className='data-qa-promo-calendar'>
      <div
        className='data-qa-promo-calendar__chart'
        style={{ width, height: `${visibleCount * MIN_HEIGHT_PER_ENTRY + AXIS_HEIGHT}px` }}
      >
        <TimelineBarChart
          timelineEntries={entries}
          timelineDateRange={dateRange}
          orderBy={OrderByOptions.DATE_ASCENDING}
          setNumVisibleEntries={ignoreRowCount}
        />
      </div>
    </div>
  );
};
